import React, { useCallback, useEffect, useState } from "react"
import api from "../../utils/api"
import { formatDateLong } from "../../utils/format"
import { getApiErrorMessage } from "../../utils/errorMessage"

function GroupJoinRequestsPanel({
  groupId = "",
  isAdmin = false,
  disabled = false,
  readOnly = false,
  onPendingCountChange,
}) {
  const [requests, setRequests] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [actingRequestId, setActingRequestId] = useState("")
  const actionsDisabled = !!disabled || !!readOnly

  const loadRequests = useCallback(async () => {
    if (!groupId || !isAdmin) {
      setRequests([])
      setError("")
      setIsLoading(false)
      if (typeof onPendingCountChange === "function") onPendingCountChange(0)
      return
    }
    setIsLoading(true)
    setError("")
    try {
      const response = await api.get(`/groups/${groupId}/join-requests/pending`)
      const rows = Array.isArray(response?.data?.requests) ? response.data.requests : []
      setRequests(rows)
      if (typeof onPendingCountChange === "function") onPendingCountChange(rows.length)
    } catch (loadError) {
      setRequests([])
      setError(getApiErrorMessage(loadError, "Failed to load join requests."))
    } finally {
      setIsLoading(false)
    }
  }, [groupId, isAdmin, onPendingCountChange])

  useEffect(() => {
    loadRequests()
  }, [loadRequests])

  const processRequest = async (requestId, action) => {
    if (!requestId || !groupId) return
    setSuccess("")
    setError("")
    setActingRequestId(String(requestId))
    try {
      const response = await api.post(`/groups/${groupId}/join-requests/${requestId}/${action}`)
      const message = String(response?.data?.message || "").trim()
      setRequests((prev) => {
        const nextRows = prev.filter((row) => String(row?.id || "") !== String(requestId))
        if (typeof onPendingCountChange === "function") onPendingCountChange(nextRows.length)
        return nextRows
      })
      setSuccess(message || (action === "approve" ? "Join request approved." : "Join request rejected."))
    } catch (actionError) {
      setError(getApiErrorMessage(actionError, `Failed to ${action} join request.`))
    } finally {
      setActingRequestId("")
    }
  }

  if (!isAdmin) return null

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
          Group Join Requests
        </h4>
        <button
          type="button"
          onClick={loadRequests}
          disabled={isLoading || actionsDisabled || !!actingRequestId}
          className="px-2 py-1 rounded border border-gray-200 text-[10px] font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {readOnly && (
        <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-700">
          Archived view is read-only. Reactivate the group to review join requests.
        </div>
      )}

      {isLoading ? (
        <p className="text-xs text-gray-500">Loading join requests...</p>
      ) : requests.length === 0 ? (
        <p className="text-xs text-gray-500">No pending join requests.</p>
      ) : (
        <div className="space-y-2">
          {requests.map((request) => {
            const requestId = String(request?.id || "")
            const isActing = requestId && requestId === String(actingRequestId || "")
            return (
              <div
                key={requestId}
                className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
              >
                <p className="text-xs font-semibold text-gray-900">
                  {request?.requesterName || "Unknown User"}
                </p>
                <p className="text-[11px] text-gray-600 mt-0.5">
                  Preferred name:{" "}
                  <span className="font-semibold text-gray-800">
                    {request?.preferredDisplayName || "-"}
                  </span>
                  {" | "}Mode:{" "}
                  <span className="font-medium text-gray-700 capitalize">
                    {request?.preferredNameMode || "registered"}
                  </span>
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Requested:{" "}
                  <span className="font-medium">
                    {request?.createdAt ? formatDateLong(request.createdAt) : "-"}
                  </span>
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => processRequest(requestId, "approve")}
                    disabled={actionsDisabled || isActing || !!actingRequestId}
                    className="px-2 py-1 rounded text-[10px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 disabled:opacity-50"
                  >
                    {isActing ? "Processing..." : "Approve"}
                  </button>
                  <button
                    type="button"
                    onClick={() => processRequest(requestId, "reject")}
                    disabled={actionsDisabled || isActing || !!actingRequestId}
                    className="px-2 py-1 rounded text-[10px] font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!!error && (
        <p className="text-xs text-red-600 mt-2">{error}</p>
      )}
      {!!success && (
        <p className="text-xs text-emerald-700 mt-2">{success}</p>
      )}
    </div>
  )
}

export default GroupJoinRequestsPanel
